// ============================================================================
// ASNE C2 — UNIT PREFERENCES STORE
// ============================================================================
//
// Per-user unit system preference, persisted to localStorage. Defaults to
// 'imperial' because the primary user base is US law enforcement who think
// in feet/mph, not meters/m-s. Users can switch to metric in Settings →
// Units. The preference is scoped to the browser (localStorage) rather than
// the server — fast, offline-capable, zero migration, and doesn't pollute
// the user profile API.
//
// Usage in components:
//   const { unitSystem } = useUnitsStore();
//   const label = fmtAltitude(track.altitudeMSL, unitSystem, 'MSL');
// ============================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UnitSystem } from '../utils/units';

interface UnitsState {
  /** Active unit system. Default: 'imperial'. */
  unitSystem: UnitSystem;

  /** Switch between imperial and metric. */
  setUnitSystem: (system: UnitSystem) => void;

  /** Convenience toggle. */
  toggleUnitSystem: () => void;
}

export const useUnitsStore = create<UnitsState>()(
  persist(
    (set) => ({
      unitSystem: 'imperial',

      setUnitSystem: (system) => set({ unitSystem: system }),

      toggleUnitSystem: () =>
        set((state) => ({
          unitSystem: state.unitSystem === 'imperial' ? 'metric' : 'imperial',
        })),
    }),
    {
      name: 'asne-units', // localStorage key
    }
  )
);
